import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  MapPin,
  BrainCircuit,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Layers,
  Droplets,
  Zap,
  Radio,
  Shield,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
} from 'recharts'
import { cn } from '@/lib/utils'
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter'
import {
  MONITORED_AREAS,
  ALERTS,
  RISK_TREND_30D,
  ALERT_COLORS,
  ALERT_BG,
  ALERT_LEVEL_LABELS,
} from '@/lib/mockData'
import { timeAgo } from '@/lib/utils'
import type { AlertLevel } from '@/types'

// ----------------------------------------------------------
// Animation variants
// ----------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.4, ease: [0.4, 0, 0.2, 1] },
  }),
}

// ----------------------------------------------------------
// Live clock hook
// ----------------------------------------------------------

function useLiveClock() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  return now
}

// ----------------------------------------------------------
// Alert level badge
// ----------------------------------------------------------

function LevelBadge({ level }: { level: AlertLevel }) {
  return (
    <span className={cn('border text-xs font-bold px-2 py-0.5 rounded', ALERT_BG[level])}>
      {level}
    </span>
  )
}

// ----------------------------------------------------------
// Mini sparkline SVG (inline, no Recharts overhead)
// ----------------------------------------------------------

function Sparkline({ data, color, height = 28 }: { data: number[]; color: string; height?: number }) {
  if (data.length < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const w = 80
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = height - ((v - min) / range) * (height - 4) - 2
    return `${x},${y}`
  })
  const pathD = `M ${points.join(' L ')}`
  const areaD = `${pathD} L ${w},${height} L 0,${height} Z`
  const gradId = `spark-${color.replace('#', '')}`

  return (
    <div className="sparkline-container">
      <svg width={w} height={height} className="overflow-visible">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#${gradId})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        <circle
          cx={(data.length - 1) / (data.length - 1) * w}
          cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
          r={2.5}
          fill={color}
        />
      </svg>
    </div>
  )
}

// ----------------------------------------------------------
// Hero stats card with sparkline
// ----------------------------------------------------------

interface StatCardProps {
  label: string
  value: number
  unit?: string
  decimals?: number
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  colorHex: string
  trend?: 'up' | 'down' | 'stable'
  trendLabel?: string
  delay?: number
  sparkData?: number[]
}

function StatCard({ label, value, unit, decimals = 0, icon: Icon, colorHex, trend, trendLabel, delay = 0, sparkData }: StatCardProps) {
  const animated = useAnimatedCounter({ to: value, decimals, duration: 1400 })

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  const trendColor =
    trend === 'up' ? 'text-terra-red' : trend === 'down' ? 'text-terra-green' : 'text-slate-500'

  return (
    <motion.div
      custom={delay}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="terra-card terra-card-glow group"
    >
      <div className="flex items-start justify-between mb-2">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110"
          style={{ background: colorHex + '18', border: `1px solid ${colorHex}30` }}
        >
          <span style={{ color: colorHex }}><Icon size={20} /></span>
        </div>
        {trend && (
          <div className={cn('flex items-center gap-1 text-xs font-medium', trendColor)}>
            <TrendIcon size={13} />
            {trendLabel}
          </div>
        )}
      </div>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="metric-value">
            {animated.toFixed(decimals)}
            {unit && <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span>}
          </div>
          <div className="metric-label">{label}</div>
        </div>
        {sparkData && sparkData.length > 2 && (
          <Sparkline data={sparkData} color={colorHex} />
        )}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Risk trend chart with zone backgrounds
// ----------------------------------------------------------

function RiskTrendChart() {
  const data = RISK_TREND_30D.map((p) => ({
    date: p.date.slice(5), // MM-DD
    score: p.score,
  }))

  return (
    <motion.div
      custom={4}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="terra-card terra-card-glow col-span-full lg:col-span-2"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">Trend Rischio — 30 Giorni</h3>
          <p className="text-xs text-slate-500 mt-0.5">Score EW aggregato · Niscemi (CL)</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-terra-green" />
            <span className="text-xs text-slate-600">&lt;5</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-terra-yellow" />
            <span className="text-xs text-slate-600">5-7</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-terra-orange" />
            <span className="text-xs text-slate-600">7-8.5</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-terra-red" />
            <span className="text-xs text-slate-600">&ge;8.5</span>
          </div>
          <div className="ml-2 px-2 py-1 rounded-md bg-terra-red/10 border border-terra-red/30">
            <span className="text-sm font-bold text-terra-red glow-text-red">8.7</span>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="riskGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* EW level zone backgrounds */}
          <ReferenceArea y1={0} y2={5} fill="#10b981" fillOpacity={0.04} />
          <ReferenceArea y1={5} y2={7} fill="#f59e0b" fillOpacity={0.04} />
          <ReferenceArea y1={7} y2={8.5} fill="#f97316" fillOpacity={0.06} />
          <ReferenceArea y1={8.5} y2={10} fill="#ef4444" fillOpacity={0.08} />
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
          <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
          <YAxis domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
            formatter={(val: number) => [val.toFixed(1), 'EW Score']}
          />
          <ReferenceLine y={8.5} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} />
          <ReferenceLine y={7.0} stroke="#f97316" strokeDasharray="4 4" strokeOpacity={0.5} />
          <ReferenceLine y={5.0} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.5} />
          <Area
            type="monotone"
            dataKey="score"
            stroke="#ef4444"
            strokeWidth={2.5}
            fill="url(#riskGradient)"
            dot={false}
            activeDot={{ r: 4, fill: '#ef4444', stroke: '#fff', strokeWidth: 1.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Top risk areas table
// ----------------------------------------------------------

function TopRiskAreas() {
  const sorted = [...MONITORED_AREAS]
    .sort((a, b) => b.ewScore - a.ewScore)
    .slice(0, 8)

  return (
    <motion.div
      custom={5}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="terra-card terra-card-glow"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">Aree a Rischio Elevato</h3>
        <Layers size={14} className="text-slate-500" />
      </div>

      <div className="space-y-1">
        {sorted.map((area, i) => {
          const barColor = ALERT_COLORS[area.level]
          return (
            <div key={area.id} className="group flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-slate-800/40 transition-colors cursor-pointer">
              <span className="text-xs text-slate-600 w-4 flex-shrink-0 tabular-nums">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <MapPin size={10} className="text-slate-500 flex-shrink-0" />
                    <span className="text-xs text-slate-200 truncate">{area.name}</span>
                    <span className="text-xs text-slate-600 truncate hidden sm:block">&middot;&nbsp;{area.province}</span>
                  </div>
                  <span className="text-xs font-bold tabular-nums ml-2 flex-shrink-0" style={{ color: barColor }}>
                    {area.ewScore.toFixed(1)}
                  </span>
                </div>
                <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(area.ewScore / 10) * 100}%` }}
                    transition={{ delay: 0.3 + i * 0.05, duration: 0.6, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{ background: barColor }}
                  />
                </div>
              </div>
              <LevelBadge level={area.level} />
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Recent alerts feed
// ----------------------------------------------------------

function AlertFeed() {
  const recent = [...ALERTS].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  return (
    <motion.div
      custom={6}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="terra-card terra-card-glow col-span-full lg:col-span-1"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Feed Allerte</h3>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-terra-red/10 border border-terra-red/30">
            <div className="w-1.5 h-1.5 rounded-full bg-terra-red animate-pulse" />
            <span className="text-xs text-terra-red font-medium">Live</span>
          </div>
        </div>
        <span className="text-xs text-slate-500">{recent.filter((a) => a.status !== 'resolved').length} attive</span>
      </div>

      <div className="space-y-2">
        {recent.map((alert) => {
          const color = ALERT_COLORS[alert.level]
          return (
            <div
              key={alert.id}
              className={cn(
                'p-3 rounded-lg border transition-all duration-200 cursor-pointer hover:bg-slate-800/40 hover:border-slate-600/60',
                alert.status === 'resolved' ? 'border-slate-800/40 opacity-50' : 'border-slate-700/40',
              )}
            >
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 data-pulse" style={{ background: color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-200 truncate">{alert.title}</p>
                    <span className="text-xs text-slate-600 flex-shrink-0">{timeAgo(alert.created_at)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{alert.municipality}, {alert.province}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <LevelBadge level={alert.level} />
                    <span className="text-xs text-slate-600">{ALERT_LEVEL_LABELS[alert.level]}</span>
                    {alert.status === 'acknowledged' && (
                      <span className="text-xs text-slate-600 ml-auto">Notificato</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Coverage stats bar
// ----------------------------------------------------------

function CoverageBar() {
  const regions = [
    { name: 'Emilia-Romagna', coverage: 87, colorClass: 'bg-terra-blue' },
    { name: 'Sicilia', coverage: 74, colorClass: 'bg-terra-purple' },
    { name: 'Calabria', coverage: 62, colorClass: 'bg-terra-cyan' },
    { name: 'Campania', coverage: 58, colorClass: 'bg-terra-green' },
    { name: 'Toscana', coverage: 45, colorClass: 'bg-terra-yellow' },
  ]

  return (
    <motion.div
      custom={3}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="terra-card terra-card-glow col-span-full"
    >
      <div className="flex items-center gap-2 mb-4">
        <Activity size={14} className="text-terra-blue" />
        <h3 className="text-sm font-semibold text-white">Copertura Monitoraggio per Regione</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {regions.map((r) => (
          <div key={r.name}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-slate-400 truncate">{r.name}</span>
              <span className="text-xs font-bold text-white">{r.coverage}%</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${r.coverage}%` }}
                transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
                className={cn('h-full rounded-full', r.colorClass)}
              />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Data pipeline status (enhanced)
// ----------------------------------------------------------

function DataPipelineStatus() {
  const pipelines = [
    { label: 'Sentinel-1C/D InSAR', last: '2h fa', icon: Activity, color: '#8b5cf6', latency: '1.2s' },
    { label: 'ARPA Pluviometri', last: '15m fa', icon: Droplets, color: '#0ea5e9', latency: '0.3s' },
    { label: 'Copernicus SWI', last: '4h fa', icon: Droplets, color: '#06b6d4', latency: '2.1s' },
    { label: 'EGMS Displacement', last: '6h fa', icon: Layers, color: '#10b981', latency: '0.8s' },
    { label: 'IFFI Inventario Frane', last: '12h fa', icon: Layers, color: '#f97316', latency: '0.5s' },
    { label: 'CLC+ Land Use 10m', last: '30g fa', icon: Layers, color: '#f59e0b', latency: '0.1s' },
  ]

  return (
    <motion.div custom={7} variants={fadeUp} initial="hidden" animate="visible" className="terra-card terra-card-glow">
      <div className="flex items-center gap-2 mb-4">
        <Zap size={14} className="text-terra-yellow" />
        <h3 className="text-sm font-semibold text-white">Pipeline Dati</h3>
        <div className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-terra-green/10 border border-terra-green/20">
          <div className="w-1.5 h-1.5 rounded-full bg-terra-green data-pulse" />
          <span className="text-xs text-terra-green font-medium">6/6 Online</span>
        </div>
      </div>
      <div className="space-y-2">
        {pipelines.map(({ label, last, icon: Icon, color, latency }) => (
          <div key={label} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/30 transition-colors group">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: color + '15', border: `1px solid ${color}25` }}
            >
              <span style={{ color }}><Icon size={12} /></span>
            </div>
            <span className="text-xs text-slate-300 flex-1">{label}</span>
            <span className="text-xs text-slate-600 font-mono hidden sm:block">{latency}</span>
            <span className="text-xs text-slate-600">{last}</span>
            <div className="w-2 h-2 rounded-full bg-terra-green flex-shrink-0" style={{ boxShadow: '0 0 6px rgba(16,185,129,0.5)' }} />
          </div>
        ))}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Main page
// ----------------------------------------------------------

export default function DashboardPage() {
  const clock = useLiveClock()
  const activeAlerts = ALERTS.filter((a) => a.status !== 'resolved')
  const criticalAlerts = ALERTS.filter((a) => a.level === 'P4' && a.status !== 'resolved')
  const monitoredCount = MONITORED_AREAS.length
  const avgEw = MONITORED_AREAS.reduce((s, a) => s + a.ewScore, 0) / MONITORED_AREAS.length

  // Sparkline data from risk trend
  const riskSparkData = RISK_TREND_30D.map((p) => p.score)
  const alertSparkData = [2, 3, 2, 4, 5, 4, 6, 5, 5, 6]
  const modelSparkData = [1, 1, 1, 2, 2, 2, 2, 2, 2, 2]

  const dateStr = clock.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = clock.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <div className="space-y-6">
      {/* Page header */}
      <motion.div variants={fadeUp} custom={0} initial="hidden" animate="visible">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              <span className="gradient-text-terra">Pannello di Controllo</span>
              <span className="text-white"> Operativo</span>
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-slate-400">
                Monitoraggio idrogeologico in tempo reale
              </p>
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-800/60 border border-slate-700/40">
                <Radio size={10} className="text-terra-green" />
                <span className="text-xs text-slate-300 font-mono tabular-nums">{dateStr} {timeStr}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {criticalAlerts.length > 0 && (
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-terra-red/10 border border-terra-red/30 text-terra-red"
                style={{ boxShadow: '0 0 20px rgba(239,68,68,0.15)' }}
              >
                <AlertTriangle size={16} />
                <span className="text-sm font-bold">{criticalAlerts.length} Allerta P4 Critica</span>
              </motion.div>
            )}
            <div className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg glass border border-terra-blue/20">
              <Shield size={14} className="text-terra-blue" />
              <span className="text-xs text-slate-300 font-medium">TERRA v0.1.0</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hero KPI row with sparklines */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Aree Monitorate"
          value={monitoredCount}
          icon={MapPin}
          colorHex="#38bdf8"
          trend="up"
          trendLabel="+3 questo mese"
          delay={0}
          sparkData={[12, 14, 15, 16, 17, 17, 18, 19, 19, 20]}
        />
        <StatCard
          label="Score EW Medio"
          value={avgEw}
          decimals={1}
          icon={Activity}
          colorHex="#f97316"
          trend="up"
          trendLabel="+1.2 da ieri"
          delay={1}
          sparkData={riskSparkData.slice(-10)}
        />
        <StatCard
          label="Allerte Attive"
          value={activeAlerts.length}
          icon={AlertTriangle}
          colorHex="#ef4444"
          trend="up"
          trendLabel="2 nuove"
          delay={2}
          sparkData={alertSparkData}
        />
        <StatCard
          label="Modelli ML Attivi"
          value={2}
          unit="/ 4"
          icon={BrainCircuit}
          colorHex="#8b5cf6"
          trend="stable"
          delay={3}
          sparkData={modelSparkData}
        />
      </div>

      {/* Coverage bar */}
      <CoverageBar />

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <RiskTrendChart />
        </div>
        <div className="lg:col-span-1">
          <AlertFeed />
        </div>
      </div>

      {/* Bottom: risk ranking + pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopRiskAreas />
        <DataPipelineStatus />
      </div>
    </div>
  )
}
